'use client';

import { Sparkles, AlertCircle } from 'lucide-react';
import { LinkedInPost } from './linkedin-post';
import { XPost } from './x-post';
import { InstagramPost } from './instagram-post';
import { VideoMedia } from './shared/video-media';
import { SocialPostActions } from './social-post-actions';

export function SpeakerPostToolUI({ part, status }: any) {
  const args = part.args;
  const result = part.result;

  if (status === 'loading' || !result) {
    return (
      <div className="animate-pulse p-4 border rounded-lg my-4 max-w-md mx-auto">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-muted" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-muted rounded w-1/3" />
            <div className="h-3 bg-muted rounded w-1/4" />
          </div>
        </div>
        <div className="mt-4 space-y-2">
          <div className="h-3 bg-muted rounded w-full" />
          <div className="h-3 bg-muted rounded w-5/6" />
          <div className="h-3 bg-muted rounded w-4/6" />
        </div>
        <p className="text-sm text-muted-foreground mt-3">
          Generating post in {args?.speaker || 'speaker'}'s voice...
        </p>
      </div>
    );
  }

  // Handle failure states with helpful messages instead of returning null
  if (!result?.success) {
    // If needs tone analysis, show that we're waiting for it
    if (result?.needsToneAnalysis) {
      return (
        <div className="p-4 border rounded-lg my-4 max-w-md mx-auto bg-muted/30">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Sparkles className="w-4 h-4 animate-pulse" />
            <span className="text-sm">Analyzing {args?.speaker || 'speaker'}'s voice first...</span>
          </div>
          {result?.message && (
            <p className="text-xs text-muted-foreground mt-2">{result.message}</p>
          )}
        </div>
      );
    }

    // Show error state for failures
    return (
      <div className="p-4 border border-destructive/30 rounded-lg my-4 max-w-md mx-auto bg-destructive/5">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm font-medium">Couldn't generate post</span>
        </div>
        {result?.message && (
          <p className="text-xs text-muted-foreground mt-2">{result.message}</p>
        )}
        {result?.error && (
          <p className="text-xs text-destructive/80 mt-1">{result.error}</p>
        )}
      </div>
    );
  }

  if (!result.post) {
    return null;
  }

  const rawPlatform = result.platform || 'linkedin';
  const platform = (rawPlatform === 'blog' ? 'linkedin' : rawPlatform) as 'linkedin' | 'twitter' | 'instagram';
  const videoMedia = result.videoContext?.playbackId ? (
    <VideoMedia
      playbackId={result.videoContext.playbackId}
      startTime={result.videoContext.startTime}
      endTime={result.videoContext.endTime}
    />
  ) : undefined;

  const PostComponent = {
    linkedin: LinkedInPost,
    twitter: XPost,
    instagram: InstagramPost,
  }[platform];

  return (
    <div className="my-4 max-w-md mx-auto">
      {result.mode === 'voice-only' && (
        <div className="text-xs text-muted-foreground mb-2 flex items-center justify-center gap-1">
          <Sparkles className="w-3 h-3 text-purple-500" />
          <span>Original content in {args?.speaker || 'speaker'}'s voice</span>
        </div>
      )}
      <PostComponent
        authorName={args?.speaker || 'Author'}
        content={result.post.body}
        media={videoMedia}
      />
      <SocialPostActions
        platform={platform}
        caption={result.post.body}
        hashtags={result.post.hashtags}
      />
      {result.toneMatch && (
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Voice match: {result.toneMatch}
        </p>
      )}
    </div>
  );
}
